import type { ClassificationContent } from '@classification-testing/domain';
import { ClassificationInputTab, ContentType } from '@classification-testing/domain';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import type { TabItem } from '@soteria/design-system-ve';
import { useMemo } from 'react';

interface UseTabManagementProps {
  activeTab: ClassificationInputTab;
  onContentChange: (content: ClassificationContent) => void;
  setPendingFile: (file: File | null) => void;
  clearError: () => void;
}

/**
 * Custom hook to handle tab management logic
 * Reduces complexity in the main component by consolidating tab configuration
 */
export function useTabManagement({ activeTab, onContentChange, setPendingFile, clearError }: UseTabManagementProps) {
  const isUsernameEnabled = useIsFeatureEnabled(FEATURE_FLAGS.USERNAME_CLASSIFICATION);

  const tabs = useMemo(
    (): TabItem[] => [
      { label: 'Enter Text' },
      { label: 'Upload Audio File' },
      { label: 'Record Audio' },
      { label: 'Classify Image' },
      ...(isUsernameEnabled ? [{ label: 'Classify Username' }] : []),
    ],
    [isUsernameEnabled]
  );

  const tabValues = useMemo(
    () => [
      ClassificationInputTab.TEXT,
      ClassificationInputTab.UPLOAD,
      ClassificationInputTab.RECORD,
      ClassificationInputTab.IMAGE_URL,
      ...(isUsernameEnabled ? [ClassificationInputTab.USERNAME] : []),
    ],
    [isUsernameEnabled]
  );

  const activeTabIndex = tabValues.indexOf(activeTab);

  const handleTabChange = (tabIndex: number) => {
    const method = tabValues[tabIndex];

    if (method) {
      onContentChange({
        type: ContentType.TEXT,
        text: '',
      });
      setPendingFile(null);
      clearError();
    }
  };

  return {
    tabs,
    tabValues,
    activeTabIndex,
    handleTabChange,
  };
}
